import type { AppProps } from 'next/app';
import React from 'react';
import { initializeContentful } from '@grubhubprod/umami-library/dist/contentful';

import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { getConfig } from 'src/utilities/config';
import { DatadogRumScript } from 'src/common/telemetry/DataDog/DatadogRumScript';
import { AppVersionScript } from 'src/components/AppVersionScript/AppVersionScript';

import { isServerSide } from 'src/utilities/isServerSide';

import { VariantsProvider } from 'src/components/VariantsProvider/VariantsProvider';
import { SdkWrapper } from 'src/components/SdkWrapper/SdkWrapper';
import { ConditionalDevCycleProvider } from 'src/components/ConditionalDevCycleProvider';

import 'src/styles/globals.scss';
import { PaymentsScript } from 'src/components/PaymentsScript/PaymentsScript';
import { PXSensorScript } from 'src/components/PXSensorScript/PXSensorScript';
// Adding this polyfill to help IE support all IANA timezones for date-fns-tz
// See links:
// https://github.com/marnusw/date-fns-tz#date-fns-tz
// https://nextjs.org/docs/basic-features/supported-browsers-features#custom-polyfills

const {
  contentful: { spaceId, cdnUrl, authToken },
} = getConfig();

initializeContentful({
  authToken,
  cdnUrl,
  spaceId,
});

function App({ Component, pageProps }: AppProps): JSX.Element {
  const { datadog, devcycleSdkKey } = getConfig();

  return (
    <HelmetProvider>
      <PXSensorScript />
      <DatadogRumScript monitorDetails={datadog} />
      <AppVersionScript
        version={process.env.PROJECT_VERSION}
        environment={process.env.GH_ENV}
      />

      {!isServerSide() && (
        <BrowserRouter>
          <ConditionalDevCycleProvider sdkKey={devcycleSdkKey}>
            <VariantsProvider>
              <SdkWrapper>
                <PaymentsScript />
                <Component {...pageProps} />
              </SdkWrapper>
            </VariantsProvider>
          </ConditionalDevCycleProvider>
        </BrowserRouter>
      )}
    </HelmetProvider>
  );
}

export default App;
